/**
 * @fileoverview The DOM / lifecycle side of the PyMOL backend: the canvas, loading the wasm
 * payload, scene-wide PyMOL settings, size, background color, camera and image capture.
 *
 * The viewer ("wrapper", pymol-web js/pymol-viewer.js) is NOT part of the BMaps bundle. It is
 * imported at run time FROM THE PAYLOAD, so wrapper, bridge.py and wheel always belong to one
 * payload version (conifer-workspace INTERFACES.md section 9). Everything here is usable
 * before that import resolves: calls are remembered and applied once the viewer exists.
 *
 * SSR / mocha safe: nothing touches `window` or `document` at import time.
 */

import {
    PYMOL_PAYLOAD_VERSION, PYMOL_PAYLOAD_DIR, PYMOL_WRAPPER_API, PYMOL_PAYLOAD_SCHEMA,
    colorToInt, pymolColorName, CartoonProfiles, RenderProfiles, DefaultCartoonProfile,
    screenCaptureScale,
} from './PyMOLConstants';

/**
 * Scene-wide PyMOL settings, applied once after start-up.
 *   transparency_picking_mode=1  transparent things stay pickable - what makes the `invisible`
 *                                style (fully transparent spheres) work; PyMOL's default (2,
 *                                "auto") picked 0 of 8 of them.
 *   auto_show_*=0                a freshly loaded object shows nothing until BMaps styles it,
 *                                so no representation is built only to be hidden again.
 */
export const SceneSettings = {
    transparency_picking_mode: 1,
    auto_show_lines: 0,
    auto_show_nonbonded: 0,
    auto_show_spheres: 0,
    auto_show_classified: 0,
};

/**
 * What the settings above are worth in a session file that someone opens in desktop PyMOL: the
 * values PyMOL itself ships (third_party/pymol-open-source layer1/SettingInfo.h), set for the
 * save and put back afterwards (saveSession).
 *
 * Only settings a `.pse` actually carries are listed. The embedded viewer's other departures -
 * `internal_gui`, `internal_feedback`, `max_threads`, `show_progress`, `use_shaders` - are
 * session-blacklisted in PyMOL (layer1/Setting.cpp is_session_blacklisted), so they never leave
 * here; nor is the embedded mouse profile saved (button assignments are not part of a session).
 */
export const DesktopSessionSettings = {
    transparency_picking_mode: 2, // auto
    auto_show_lines: 1,
    auto_show_nonbonded: 1,
    auto_show_spheres: 0,
    auto_show_classified: -1, // auto
    auto_zoom: -1, // auto; the embedded viewer sets 0 (the host frames the scene)
    internal_gui_width: 220,
};

/**
 * URL of the payload directory, with a trailing slash.
 *
 * Contract: `${NEXT_PUBLIC_BASE_PATH}/pymol/<version>/`. next.config.js inlines that variable
 * for the Next build (BASE_PATH=/app). The legacy webpack build and its dev server (pages under
 * /dev/) do not define it; there the payload sits next to the page, exactly like the
 * `ketcher/` directory that Ketcher.jsx addresses page-relative.
 * @param {{href: string}} [location]
 * @returns {string}
 */
export function getPayloadBaseURL(location) {
    const tail = `${PYMOL_PAYLOAD_DIR}/${PYMOL_PAYLOAD_VERSION}/`;
    // Must stay a literal `process.env.NEXT_PUBLIC_BASE_PATH` for Next to substitute it.
    const basePath = typeof process !== 'undefined' && process.env
        ? process.env.NEXT_PUBLIC_BASE_PATH
        : undefined;
    if (typeof basePath === 'string' && basePath !== '') {
        return `${basePath.replace(/\/+$/, '')}/${tail}`;
    }
    const loc = location || (typeof window !== 'undefined' ? window.location : null);
    if (!loc) return `/${tail}`;
    return new URL(tail, new URL('.', loc.href)).href;
}

/** Does this wrapper speak the API the backend was written for? */
export function wrapperApiIsCompatible(apiVersion, required=PYMOL_WRAPPER_API) {
    const have = String(apiVersion || '').split('.');
    const want = String(required).split('.');
    if (have[0] !== want[0]) return false;
    return want[0] !== '0' || have[1] === want[1];
}

/** Failed imports of the wrapper module on this page (see loadViewerFromPayload). */
let failedImports = 0;

/**
 * Default viewer loader: import the wrapper from the payload and start it.
 * Replaceable (InitViewer option `viewerLoader`) so tests can supply a recording fake.
 * @param {{canvas: HTMLCanvasElement, payloadURL: string, onProgress?: function}} args
 * @returns {Promise<object>} a started PyMolViewer
 */
export async function loadViewerFromPayload({ canvas, payloadURL, onProgress }) {
    // A failed dynamic import stays failed for the life of the page (the browser's module map
    // keeps the failure), so after one the retry imports the same file under a new URL - found
    // when a retry after the payload became reachable again failed without a request.
    const retry = failedImports > 0 ? `?retry=${failedImports}` : '';
    const moduleURL = `${payloadURL}js/pymol-viewer.js${retry}`;
    let PyMolViewer;
    try {
        // A run-time import of a URL the bundler must not try to resolve, in either toolchain.
        ({ PyMolViewer } = await import(/* webpackIgnore: true */ moduleURL));
    } catch (err) {
        failedImports += 1;
        throw err;
    }
    if (!PyMolViewer || !wrapperApiIsCompatible(PyMolViewer.API_VERSION)) {
        throw new Error(`3D viewer payload at ${payloadURL} has wrapper API `
            + `${PyMolViewer && PyMolViewer.API_VERSION}; BMaps needs ${PYMOL_WRAPPER_API}.x`);
    }
    const viewer = await PyMolViewer.create({
        canvas, payloadURL, verifyIntegrity: true, onProgress,
    });
    const manifest = viewer.manifest || {};
    if (manifest.schema !== PYMOL_PAYLOAD_SCHEMA || manifest.version !== PYMOL_PAYLOAD_VERSION) {
        console.warn(`3D viewer payload reports version ${manifest.version} (schema ${manifest.schema}); BMaps pins ${PYMOL_PAYLOAD_VERSION}.`);
    }
    return viewer;
}

export class PyMOLScene {
    /**
     * @param {object} options
     * @param {string} [options.parentEltId] id of the wrapper element (`canvas_wrapper`)
     * @param {object} [options.parentElement] the wrapper element itself (tests)
     * @param {function} [options.viewerLoader] see loadViewerFromPayload
     * @param {string} [options.payloadURL] override of getPayloadBaseURL() (tests, harnesses).
     *   Deliberately NOT read from the page URL: it names the JavaScript the page will run.
     */
    constructor({
        parentEltId, parentElement, viewerLoader, payloadURL,
    }={}) {
        this.parentEltId = parentEltId;
        this.parentElement = parentElement || null;
        this.viewerLoader = viewerLoader || loadViewerFromPayload;
        this.payloadURL = payloadURL || null;
        this.canvas = null;
        this.statusElt = null;
        /** @type {object|null} the PyMolViewer once it is ready */
        this.viewer = null;
        this.failed = null;
        this.disposed = false;
        this.backgroundColor = null;
        /** the CartoonStyle preference in force; applySceneSettings reapplies it */
        this.cartoonProfile = DefaultCartoonProfile;
        this.readyPromise = null;
        this.dprQuery = null;
        this.onDprChange = () => { this.watchDevicePixelRatio(); this.resize(); };
        /** unsubscribe functions of the context-loss listeners */
        this.offContext = null;
    }

    get ready() { return !!this.viewer; }

    /**
     * Create the canvas as a DIRECT child of the wrapper element. `#canvas_wrapper canvas`
     * is queried by paste_drag.js right after the visualizer is constructed, so this has to
     * happen synchronously, long before the viewer is ready.
     */
    createCanvas() {
        if (this.canvas) return this.canvas;
        const doc = typeof document !== 'undefined' ? document : null;
        const parent = this.parentElement
            || (doc && this.parentEltId ? doc.getElementById(this.parentEltId) : null);
        if (!parent || !(doc || parent.ownerDocument)) return null;
        this.parentElement = parent;
        const canvas = (parent.ownerDocument || doc).createElement('canvas');
        // Same placement Visualizer3Dmol's canvas gets from 3Dmol.
        Object.assign(canvas.style, {
            position: 'absolute',
            top: '0px',
            left: '0px',
            width: '100%',
            height: '100%',
            padding: '0',
            zIndex: '0',
            outline: 'none',
        });
        // The wrapper focuses the canvas on pointerdown (and cancels the compat mousedown that
        // bfm_viewer.js makeCanvasFocusable() listens for). The canvas sits inside the wrapper
        // element, so utils.focusIsInCanvas() holds either way. -1: focusable, but not a tab stop.
        canvas.tabIndex = -1;
        canvas.setAttribute('aria-label', '3D molecular viewer');
        parent.appendChild(canvas);
        this.canvas = canvas;
        return canvas;
    }

    /** Small, click-through status line while the payload loads, and the error if it fails. */
    setStatus(text) {
        if (!this.parentElement || !this.canvas) return;
        if (!text) {
            if (this.statusElt) this.statusElt.remove();
            this.statusElt = null;
            return;
        }
        if (!this.statusElt) {
            const elt = this.canvas.ownerDocument.createElement('div');
            Object.assign(elt.style, {
                position: 'absolute',
                left: '50%',
                bottom: '1.5em',
                transform: 'translateX(-50%)',
                padding: '0.25em 0.75em',
                borderRadius: '4px',
                font: '13px sans-serif',
                color: '#ffffff',
                background: 'rgba(0, 0, 0, 0.55)',
                pointerEvents: 'none',
                zIndex: '1',
            });
            elt.setAttribute('role', 'status');
            this.parentElement.appendChild(elt);
            this.statusElt = elt;
        }
        this.statusElt.textContent = text;
    }

    /**
     * Start loading. Resolves with the viewer; rejects (after logging) when the payload cannot
     * be loaded - the backend then keeps buffering and never throws at its callers.
     * @returns {Promise<object>}
     */
    start() {
        if (this.readyPromise) return this.readyPromise;
        const canvas = this.createCanvas();
        if (!canvas) {
            this.failed = new Error('no parent element for the 3D canvas');
            this.readyPromise = Promise.reject(this.failed);
            this.readyPromise.catch(() => {});
            return this.readyPromise;
        }
        const payloadURL = this.payloadURL || getPayloadBaseURL();
        this.setStatus('Loading 3D viewer…');
        this.readyPromise = Promise.resolve()
            .then(() => this.viewerLoader({
                canvas, payloadURL, onProgress: (stage) => this.setStatus(`Loading 3D viewer… (${stage})`),
            }))
            .then((viewer) => {
                if (this.disposed) {
                    viewer.dispose();
                    throw new Error('3D viewer was disposed while loading');
                }
                this.viewer = viewer;
                this.applySceneSettings();
                this.watchDevicePixelRatio();
                this.watchContextLoss(viewer);
                this.setStatus(null);
                if (this.active === false) viewer.stopLoop(); // switched away while loading
                const notice = viewer.manifest && viewer.manifest.notice;
                if (notice) console.log(notice);
                return viewer;
            })
            .catch((err) => {
                this.failed = err;
                if (!this.disposed) {
                    console.error(`3D viewer failed to load from ${payloadURL}`, err);
                    // The retry re-imports the wrapper under a new URL, but the wrapper's own
                    // static imports (projection.js ...) keep their URLs, and the browser keeps a
                    // failed one failed for the life of the page: a second failure needs a reload.
                    this.setStatus(failedImports > 1
                        ? 'The 3D viewer failed to load again; reload the page to try once more.'
                        : 'The 3D viewer failed to load (see the browser console).');
                }
                throw err;
            });
        this.readyPromise.catch(() => {});
        return this.readyPromise;
    }

    /**
     * WebGL context loss (GPU reset, driver update, too many contexts, a backgrounded mobile tab).
     * The wrapper (payload >= 0.1.1) stops drawing when the context is lost and, when the browser
     * restores it, rebuilds PyMOL's GL side on its own: the scene goes through a session into a
     * fresh PyMOL instance, so every object, representation, colour, setting and the view carry
     * over and this backend's model stays valid. Here: tell the user what is going on, and
     * re-assert the scene settings once recovered (they travel in the session too; cheap).
     */
    watchContextLoss(viewer) {
        if (typeof viewer.on !== 'function') return;
        this.offContext = [
            viewer.on('contextlost', () => {
                console.warn('PyMOL backend: WebGL context lost; waiting for the browser to restore it');
                this.setStatus('3D view interrupted by the graphics system; restoring…');
            }),
            viewer.on('contextrestored', (result) => {
                if (result && result.recovered) {
                    console.log(`PyMOL backend: WebGL context restored, scene recovered in ${result.ms} ms`);
                    this.applySceneSettings();
                    this.setStatus(null);
                    if (this.active === false) viewer.stopLoop(); // the wrapper restarted it
                } else {
                    console.error('PyMOL backend: WebGL context restored, but the scene could not be recovered',
                        result && result.error);
                    this.setStatus('The 3D view could not be restored after a graphics error. Save your work and reload the page.');
                }
            }),
        ];
    }

    applySceneSettings() {
        const { viewer } = this;
        for (const [name, value] of Object.entries(SceneSettings)) {
            try {
                viewer.set(name, value);
            } catch (ex) {
                console.warn(`PyMOL backend: cannot set ${name}`, ex);
            }
        }
        if (this.backgroundColor != null) this.setBackgroundColor(this.backgroundColor);
        this.applyCartoonProfile(this.cartoonProfile);
    }

    /**
     * Ribbon proportions of the `CartoonStyle` preference (CartoonProfiles). Remembered, so the
     * start-up and a context-loss recovery apply it too; PyMOL rebuilds its cartoons by itself
     * when these settings change. An unknown name falls back to PyMOL's own defaults.
     * @param {string} name
     */
    applyCartoonProfile(name) {
        const profile = CartoonProfiles[name] || CartoonProfiles[DefaultCartoonProfile];
        this.cartoonProfile = CartoonProfiles[name] ? name : DefaultCartoonProfile;
        const { viewer } = this;
        if (!viewer) return; // applied by applySceneSettings once the viewer is ready
        for (const [setting, value] of Object.entries(profile)) {
            try {
                viewer.set(setting, value);
            } catch (ex) {
                console.warn(`PyMOL backend: cannot set ${setting}`, ex);
            }
        }
    }

    /**
     * The wrapper resizes on a ResizeObserver, which does not fire when only the device pixel
     * ratio changes (browser zoom, window dragged to another monitor).
     */
    watchDevicePixelRatio() {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
        if (this.dprQuery) this.dprQuery.removeEventListener('change', this.onDprChange);
        this.dprQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
        this.dprQuery.addEventListener('change', this.onDprChange);
    }

    /**
     * Live switching: while inactive the canvas and status line are hidden and the wrapper's
     * frame loop is stopped (a payload still loading stops it once ready, see start()).
     */
    setActive(active) {
        this.active = active;
        const display = active ? '' : 'none';
        if (this.canvas) this.canvas.style.display = display;
        if (this.statusElt) this.statusElt.style.display = display;
        if (!this.viewer) return;
        if (active) {
            this.viewer.startLoop();
            this.resize();
        } else {
            this.viewer.stopLoop();
        }
    }

    /** Re-read the CSS size and device pixel ratio; the wrapper sets the backing store. */
    resize() {
        if (this.viewer) this.viewer.resize();
    }

    /** @param {number|string|{hex: number}} colorInfo */
    setBackgroundColor(colorInfo) {
        const color = colorToInt(colorInfo);
        if (color == null) {
            console.warn(`PyMOL backend: unrecognized background color ${JSON.stringify(colorInfo)}`);
            return;
        }
        this.backgroundColor = color;
        if (this.canvas) this.canvas.style.backgroundColor = `#${color.toString(16).padStart(6, '0')}`;
        if (this.viewer) {
            this.viewer.cmd.bg_color(pymolColorName(color));
        }
    }

    /** @returns {number[]|null} PyMOL's 18-float view, null before the viewer is ready */
    getView() {
        return this.viewer ? Array.from(this.viewer.getView()) : null;
    }

    setView(view) {
        if (this.viewer) this.viewer.setView(view);
    }

    /**
     * The screen frame as an image, at 3Dmol's export size (screenCaptureScale): the wrapper
     * (payload >= 0.1.3) re-renders once at that size in the same task when it differs from the
     * backing store, and puts the screen back. draw() + canvas.toBlob() in the same task - the
     * drawing buffer is not preserved.
     */
    toBlob(options={}) {
        if (!this.viewer) return Promise.resolve(null);
        const scale = screenCaptureScale(this.viewer.dpr);
        return this.viewer.toBlob({ type: options.type, scale }).catch((ex) => {
            console.error('PyMOL backend: image capture failed', ex);
            return null;
        });
    }

    /** @returns {{width: number, height: number}|null} the canvas drawing buffer (CSS x DPR) */
    pixelSize() {
        const canvas = this.viewer ? this.viewer.canvas : this.canvas;
        if (!canvas || !canvas.width || !canvas.height) return null;
        return { width: canvas.width, height: canvas.height };
    }

    /**
     * A ray-traced PNG of the current view, rendered by PyMOL's CPU ray tracer (cmd.png with
     * ray=1). This BLOCKS the main thread for the whole render (seconds for a big scene).
     * `ray_trace_mode` and `ray_opaque_background` are set for this render only and restored
     * afterwards, whatever happens - the user may have set them through the command line. So
     * are the current CartoonStyle profile's render settings (RenderProfiles: Maestro's ambient
     * occlusion and angle-dependent transparency) when the profile is not PyMOL's own.
     * `dpi` is written into the PNG as its resolution (how big the image is meant to be on paper);
     * it does not change the pixel size, which is `width` x `height`.
     * @param {{width: number, height: number, dpi?: number, outline?: boolean,
     *          transparentBackground?: boolean}} options
     * @returns {Promise<Blob|null>}
     */
    async rayTrace({
        width, height, dpi=0, outline=false, transparentBackground=false,
    }) {
        const { viewer } = this;
        if (!viewer) return null;
        const temporary = {
            // 1 = normal colours + outlines (in ray_trace_color); 0 = normal
            ray_trace_mode: outline ? 1 : 0,
            // explicit 0/1: the default (-1) follows `opaque_background`
            ray_opaque_background: transparentBackground ? 0 : 1,
        };
        if (outline && transparentBackground) {
            // ray_trace_color defaults to "front", the colour that contrasts with the background:
            // white outlines on BMaps' default black. A transparent image goes onto pages of
            // unknown (usually light) colour, so its outlines are black.
            temporary.ray_trace_color = 'black';
        }
        if (this.cartoonProfile !== DefaultCartoonProfile && RenderProfiles[this.cartoonProfile]) {
            Object.assign(temporary, RenderProfiles[this.cartoonProfile]);
        }
        const saved = {};
        try {
            for (const [name, value] of Object.entries(temporary)) {
                saved[name] = viewer.cmd.get(name);
                viewer.cmd.set(name, value);
            }
            const request = { ray: true, width, height };
            if (dpi > 0) request.dpi = dpi; // else PyMOL's -1: no resolution in the file
            return await viewer.png(request);
        } catch (ex) {
            console.error('PyMOL backend: ray-traced image failed', ex);
            return null;
        } finally {
            for (const [name, value] of Object.entries(saved)) {
                try {
                    viewer.cmd.set(name, value);
                } catch (ex) {
                    console.warn(`PyMOL backend: could not restore ${name}`, ex);
                }
            }
        }
    }

    /**
     * The scene as PyMOL session bytes (`cmd.save`, a `.pse`). Export only: BMaps never loads one
     * back, because a session is a Python pickle and loading it is code execution.
     *
     * `desktop` puts the embed-only settings (DesktopSessionSettings) into the file and restores
     * what was there afterwards, whatever happens - as rayTrace does for its render settings. The
     * whole thing is synchronous, so no frame is drawn with the temporary values.
     * @param {{desktop?: boolean}} [options]
     * @returns {Uint8Array|null}
     */
    saveSession({ desktop=true }={}) {
        const { viewer } = this;
        if (!viewer) return null;
        const saved = {};
        try {
            if (desktop) {
                for (const [name, value] of Object.entries(DesktopSessionSettings)) {
                    saved[name] = viewer.cmd.get(name);
                    viewer.cmd.set(name, value);
                }
            }
            return viewer.saveSession();
        } finally {
            for (const [name, value] of Object.entries(saved)) {
                try {
                    viewer.cmd.set(name, value);
                } catch (ex) {
                    console.warn(`PyMOL backend: could not restore ${name}`, ex);
                }
            }
        }
    }

    dispose() {
        this.disposed = true;
        if (this.dprQuery) this.dprQuery.removeEventListener('change', this.onDprChange);
        this.dprQuery = null;
        for (const off of this.offContext || []) if (typeof off === 'function') off();
        this.offContext = null;
        if (this.viewer) {
            try { this.viewer.dispose(); } catch (ex) { console.warn('PyMOL backend: dispose failed', ex); }
            this.viewer = null;
        }
        this.setStatus(null);
        if (this.canvas) this.canvas.remove();
        this.canvas = null;
    }
}
